import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import GenericTable, { ColumnDefinition } from "../../../common/generic-table";
import { Airframe } from "../../../../types/airframe";
import { Component } from "../../../../types/component";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import {
  AIRFRAME_FIELD_SECTIONS,
  formatAirframeCellValue,
  getAirframeOwnershipFields,
} from "./airframeAttributeFields";
import type { DisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { AirframeActionButtons } from "./AirframeActionButtons";
import { AirframeAssemblySubGridInline } from "./AirframeAssemblySubGrid";

type Props = {
  airframes: Airframe[];
  components: Component[];
  displayPreferences: DisplayPreferences;
  getInstalledComponentsForAirframe: (airframe: Airframe) => Component[];
  onAttach: (p: Airframe) => void;
  onEdit: (p: Airframe) => void;
  onDelete: (p: Airframe) => void;
  onMiniPack: (p: Airframe) => void;
  onReplace: (p: Airframe, c: Component) => void;
  onDetach: (componentId: string, airframeId?: string, slotId?: string) => void;
};

export function AirframeGridTable({
  airframes,
  components,
  displayPreferences,
  getInstalledComponentsForAirframe,
  onAttach,
  onEdit,
  onDelete,
  onMiniPack,
  onDetach,
}: Props) {
  const canDetachComponent = useHasPermission("assets-airframes", "delete");

  const visibleAirframes = useMemo(
    () => airframes.filter((p) => p.tailNumber),
    [airframes]
  );

  const [expandedAirframeId, setExpandedAirframeId] = useState<string | null>(null);
  const didSetInitialExpand = useRef(false);

  useEffect(() => {
    if (visibleAirframes.length === 0) {
      setExpandedAirframeId(null);
      didSetInitialExpand.current = false;
      return;
    }

    const expandedStillVisible =
      expandedAirframeId !== null &&
      visibleAirframes.some((row) => row.id === expandedAirframeId);

    if (expandedAirframeId && !expandedStillVisible) {
      setExpandedAirframeId(visibleAirframes[0].id);
      return;
    }

    if (!didSetInitialExpand.current) {
      setExpandedAirframeId(visibleAirframes[0].id);
      didSetInitialExpand.current = true;
    }
  }, [visibleAirframes, expandedAirframeId]);

  const handleRowClick = (row: Airframe) => {
    setExpandedAirframeId((current) => (current === row.id ? null : row.id));
  };

  const stopRowToggle = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const expandColumn: ColumnDefinition<Airframe> = {
    id: "_expand",
    label: "",
    minWidth: 48,
    align: "center",
    format: (row) => {
      const isExpanded = expandedAirframeId === row.id;
      return (
        <IconButton
          size="small"
          aria-label={isExpanded ? "Collapse attached assemblies" : "Expand attached assemblies"}
          aria-expanded={isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          sx={{
            color: isExpanded ? "hsl(19 101% 51% / 1)" : "#888",
            p: 0.5,
          }}
        >
          {isExpanded ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      );
    },
  };

  const ownershipFields = useMemo(
    () => getAirframeOwnershipFields(displayPreferences),
    [displayPreferences]
  );

  const attributeColumns: ColumnDefinition<Airframe>[] = useMemo(
    () =>
      [
        ...AIRFRAME_FIELD_SECTIONS.flatMap((section) => section.fields),
        ...ownershipFields,
      ].map((field) => ({
        id: field.id,
        label: field.label,
        minWidth: field.id === "ownershipStatus" ? 140 : 110,
        format: (row: Airframe) => formatAirframeCellValue(field.getValue(row)),
      })),
    [ownershipFields]
  );

  const columns = [expandColumn, ...attributeColumns];

  return (
    <Box className="airframe-grid-table">
      <Box
        sx={{
          "& .generic-table-container": {
            overflowX: "auto",
          },
          "& .generic-table-header-cell": {
            backgroundColor: "hsl(205 45% 28%) !important",
            color: "hsl(205 85% 88%) !important",
            borderBottom: "2px solid hsl(205 45% 38%) !important",
            whiteSpace: "nowrap",
            fontSize: "0.8rem",
          },
          "& .generic-table-body-cell": {
            fontSize: "0.85rem",
            verticalAlign: "middle",
            whiteSpace: "nowrap",
          },
          "& .generic-table-row:nth-of-type(even)": {
            backgroundColor: "#161616 !important",
          },
          "& .generic-table-detail-row": {
            backgroundColor: "#1a1a1a !important",
          },
          "& .generic-table-row-clickable": {
            cursor: "pointer",
          },
        }}
      >
        <Typography variant="body2" sx={{ color: "#888", mb: 1 }}>
          Use the arrow or click a row to expand attached assemblies below it.
        </Typography>

        <GenericTable
          columns={columns}
          data={visibleAirframes}
          getRowKey={(row) => row.id}
          emptyMessage="No airframes"
          showResultsCount={false}
          onRowClick={handleRowClick}
          selectedRowKey={expandedAirframeId}
          renderDetailRow={(row) => (
            <AirframeAssemblySubGridInline
              airframe={row}
              components={components}
              getInstalledComponentsForAirframe={getInstalledComponentsForAirframe}
              onDetach={onDetach}
              canDetach={canDetachComponent}
            />
          )}
          actionsColumn={{
            label: "Actions",
            align: "right",
            render: (row) => (
              <Box onClick={stopRowToggle}>
                <AirframeActionButtons
                  pkg={row}
                  onAttach={onAttach}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onMiniPack={onMiniPack}
                  inline
                />
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
}
